/**
 * CharmOptionsRenderer - Lucky Charm node options configuration
 *
 * Handles theme, background, font, and other customization options.
 * Supports field mapping via FieldSourceToggle for the phrase field.
 */

import React from 'react';
import { Input } from '@/components/ui/Input';
import {
  useLuckyCharmThemes,
  useLuckyCharmImages,
  useLuckyCharmFonts,
} from '@/features/playground/hooks/usePlayground';
import { FieldSourceToggle } from '../FieldSourceToggle';
import type { BaseCustomRendererProps } from './types';
import { configFieldKey } from './types';

export const CharmOptionsRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  // Fetch Lucky Charm resources
  const currentThemeId = nodeData.background_set_id as string | undefined;
  const { data: themes = [], isLoading: themesLoading } = useLuckyCharmThemes();
  const { data: images = [], isLoading: imagesLoading } = useLuckyCharmImages(
    currentThemeId || undefined
  );
  const { data: fontsData, isLoading: fontsLoading } = useLuckyCharmFonts();
  const fonts = fontsData?.fonts || [];

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  // Create field handlers for phrase field
  const phraseFieldKey = configFieldKey('phrase');
  const phraseHandlers = fieldSourceHandlersFactory?.createFieldHandlers(phraseFieldKey);

  return (
    <div className="space-y-4">
      {/* Required: Phrase */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">必填项</span>
        </div>

        <FieldSourceToggle
          fieldKey={phraseFieldKey}
          label="签文内容"
          description="支持变量: {{变量名}} 引用输入字段"
          required={true}
          availableSources={availableSources}
          customInputs={customInputs}
          contextVariables={contextVariables}
          startVariables={startVariables}
          mapping={phraseHandlers?.mapping}
          onMappingChange={phraseHandlers?.onMappingChange || (() => {})}
          onKeyPathChange={phraseHandlers?.onKeyPathChange || (() => {})}
          onMappingRemove={phraseHandlers?.onMappingRemove || (() => {})}
          disabled={isDisabled}
          compatibleTypes={['string']}
        >
          <textarea
            value={(nodeData.phrase as string) || ''}
            onChange={(e) => handleUpdate('phrase', e.target.value || undefined)}
            rows={2}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
            placeholder="输入签文内容，如：今日宜静心思考...&#10;支持变量替换: {{phrase}}"
          />
        </FieldSourceToggle>
      </div>

      {/* Customization Options */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">图片生成选项</span>
          <span className="text-xs text-gray-400">(可选)</span>
        </div>

        {/* Emotion Trend */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">情绪趋势</label>
          <select
            value={(nodeData.emotion_trend as string) || ''}
            onChange={(e) => handleUpdate('emotion_trend', e.target.value || undefined)}
            disabled={isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
          >
            <option value="">自动</option>
            <option value="积极">积极 (Positive)</option>
            <option value="平静">平静 (Calm)</option>
            <option value="希望">希望 (Hopeful)</option>
            <option value="感恩">感恩 (Grateful)</option>
            <option value="忧虑">忧虑 (Concerned)</option>
          </select>
        </div>

        {/* Chart ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">星盘 ID</label>
          <Input
            value={(nodeData.chart_id as string) || ''}
            onChange={(e) => handleUpdate('chart_id', e.target.value || undefined)}
            placeholder="留空使用用户默认星盘"
            disabled={isDisabled}
          />
        </div>

        {/* Background Theme (Set) Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            背景套件
            {themesLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
          </label>
          <select
            value={(nodeData.background_set_id as string) || ''}
            onChange={(e) => {
              handleUpdate('background_set_id', e.target.value || undefined);
              handleUpdate('background_image_id', undefined);
            }}
            disabled={themesLoading || isDisabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
          >
            <option value="">所有套件 (随机)</option>
            {themes.map((theme) => (
              <option key={theme.id} value={theme.id} title={theme.description}>
                {theme.display_name} ({theme.image_count}张)
              </option>
            ))}
          </select>
          {nodeData.background_set_id && (
            <p className="text-xs text-gray-500 mt-1">
              {themes.find((t) => t.id === nodeData.background_set_id)?.description}
            </p>
          )}
        </div>

        {/* Background Image and Font Selectors */}
        <div className="grid grid-cols-2 gap-3">
          {/* Background Image Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              背景图
              {imagesLoading && <span className="ml-2 text-gray-400 text-xs">...</span>}
            </label>
            <select
              value={(nodeData.background_image_id as string) || ''}
              onChange={(e) => handleUpdate('background_image_id', e.target.value || undefined)}
              disabled={imagesLoading || isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
            >
              <option value="">
                随机{nodeData.background_set_id ? ' (当前套件)' : ''}
              </option>
              {images.map((img) => (
                <option key={img.id} value={img.id} title={img.filename}>
                  {img.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Font Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              字体
              {fontsLoading && <span className="ml-2 text-gray-400 text-xs">...</span>}
            </label>
            <select
              value={(nodeData.font_id as number) || ''}
              onChange={(e) =>
                handleUpdate('font_id', e.target.value ? Number(e.target.value) : undefined)
              }
              disabled={fontsLoading || isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
            >
              <option value="">默认字体</option>
              {fonts.map((font) => (
                <option key={font.id} value={font.id}>
                  {font.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharmOptionsRenderer;
